import React, { useEffect } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputText } from 'src/components';
import { SText } from 'src/components/SText';
import { AdvisorCatalogLoader } from 'src/containers/advisorCatalogLoader';
import { FormsErrorParser } from 'src/containers/errors';
import { ClientFormColisCountAndDescriptionInput } from 'src/types';
import { FooterActions } from '../FooterActions';
import { useClientColisNumberAndDescriptionForm } from '../hooks/useClientColisNumberAndDescriptionForm';

type Props = {
  testID?: string;
  defaultValues: Partial<ClientFormColisCountAndDescriptionInput>;
  onFieldsUpdate: (fields: ClientFormColisCountAndDescriptionInput) => void;
  onPreview: () => void;
  onSubmit: () => void;
};

export const CreateColisNumberDescriptionForm: React.FC<Props> = ({
  onPreview,
  onSubmit,
  onFieldsUpdate,
  defaultValues,
  testID = 'CreateColisNumberDescriptionForm',
}) => {
  const { t } = useTranslation();

  const { handleSubmit, control, watch, errors } =
    useClientColisNumberAndDescriptionForm<ClientFormColisCountAndDescriptionInput>({
      defaultValues,
    });

  useEffect(() => {
    const subscription = watch((value, { name, type }) => onFieldsUpdate(value as unknown as any));
    return () => subscription.unsubscribe();
  }, [watch, onFieldsUpdate]);

  return (
    <div data-testid={testID} className="">
      <div>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="colisNumber"
            control={control}
            render={({ field: { onBlur, onChange, value } }) => {
              return (
                <>
                  <SText
                    label="estore-details.nb-count-colis"
                    classNames="my-2 text-gray-500 font-light text-lg block"
                  />
                  <InputText
                    placeholder="estore-details.nb-count-colis-placeholder"
                    error={errors?.colisNumber}
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    type="number"
                  />
                </>
              );
            }}
          />
          <div>
            <Controller
              name="description"
              control={control}
              render={({ field: { onBlur, onChange, value } }) => {
                const customClasses = !errors.description
                  ? 'border-gray-200 text-gray-600'
                  : 'border-red-500 text-red-800';

                return (
                  <>
                    <SText
                      label="estore-details.description-title"
                      classNames="my-2 text-gray-500 font-light text-lg block"
                    />
                    <textarea
                      onChange={onChange}
                      placeholder={t('estore-details.description-placeholder')}
                      value={value}
                      className={`focus:outline-none font-light  placeholder-gray-300 border   w-full rounded bg-white bg-clip-padding p-2 ${customClasses}`}
                      rows={6}
                      onBlur={onBlur}
                    ></textarea>
                  </>
                );
              }}
            />
            {!!errors?.description && (
              <div className="my-1">
                <FormsErrorParser error={errors?.description} />
              </div>
            )}
          </div>
          <div>
            <AdvisorCatalogLoader zone="EStoreUnderDescription" testID="AdvisorCatalogLoader" />
          </div>
          <FooterActions nextLabel="global.next" previewLabel="global.navigation-back" onPreview={onPreview} />
        </form>
      </div>
    </div>
  );
};
